import api from './api';

// ============================================
// TYPES
// ============================================

export type TipoAnunciante = 'PAGALI' | 'TERCEIROS';
export type StatusCampanha = 'ativo' | 'inativo' | 'agendado' | 'expirado';
// 'APP' = áreas das páginas de categoria (app e site), separador App
export type Plataforma = 'WEB' | 'MOBILE' | 'APP';
export type TipoSpot = 'BANNER' | 'FULLSCREEN' | 'NOTIFICATION';

export interface Advertisement {
  id: string;
  codigo: string;
  titulo: string;
  subtitulo?: string;
  descricao?: string;
  tipoAnunciante: TipoAnunciante;
  spotId?: string;
  spotNome?: string;
  spotPlataforma?: Plataforma;
  spotArea?: string;
  temImagem?: boolean;
  imagem?: string;
  mimeType?: string;
  linkUrl?: string;
  entidadeId?: string;
  entidadeNome?: string;
  dataInicio?: string;
  dataFim?: string;
  prioridade: number;
  visualizacoes: number;
  cliques: number;
  orcamento?: number;
  gasto?: number;
  estado: string;
  statusCampanha: StatusCampanha;
  dataRegisto: string;
}

export interface AdvertisementInput {
  codigo: string;
  titulo: string;
  subtitulo?: string;
  descricao?: string;
  tipoAnunciante: TipoAnunciante;
  spotId?: string;
  imagem?: string;
  mimeType?: string;
  linkUrl?: string;
  entidadeId?: string;
  dataInicio?: string;
  dataFim?: string;
  prioridade?: number;
  orcamento?: number;
  estado?: string;
}

export interface AdSpot {
  id: string;
  codigo: string;
  nome: string;
  descricao?: string;
  tipo: TipoSpot;
  largura: number;
  altura: number;
  plataforma: Plataforma;
  area: string;
  estado: string;
  adsCount: number;
  dataRegisto: string;
}

export interface AdSpotInput {
  codigo: string;
  nome: string;
  descricao?: string;
  tipo?: TipoSpot;
  largura?: number;
  altura?: number;
  plataforma?: Plataforma;
  area?: string;
  estado?: string;
}

export interface AdvertisingStats {
  pagali: {
    total: number;
    ativas: number;
    totalVisualizacoes: number;
    totalCliques: number;
  };
  terceiros: {
    total: number;
    ativas: number;
    totalVisualizacoes: number;
    totalCliques: number;
    totalOrcamento: number;
    totalGasto: number;
  };
  /** Áreas das páginas de categoria (separador App) */
  app?: {
    total: number;
    ativas: number;
    totalVisualizacoes: number;
    totalCliques: number;
    totalOrcamento: number;
    totalGasto: number;
  };
}

/** Área de publicidade de uma página de categoria (app e site) — spot CATnn */
export interface AppSpot {
  id: string;
  codigo: string;
  nome: string;
  descricao?: string;
  estado: string;
  categoriaId: number;
  totalAnuncios: number;
  anunciosNoAr: number;
}

export interface AdvertisingListResponse {
  success: boolean;
  data: Advertisement[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  stats: AdvertisingStats;
}

export interface AdSpotListResponse {
  success: boolean;
  data: AdSpot[];
}

// Anunciantes (External Advertisers)
export interface Anunciante {
  id: string;
  codigo: string;
  nome: string;
  nif?: string;
  email?: string;
  telefone?: string;
  endereco?: string;
  contactoNome?: string;
  contactoEmail?: string;
  contactoTelefone?: string;
  temLogo?: boolean;
  logo?: string;
  logoMimeType?: string;
  limiteCredito: number;
  saldo: number;
  notas?: string;
  estado: string;
  dataRegisto: string;
  adsCount?: number;
  totalVisualizacoes?: number;
  totalCliques?: number;
  totalGasto?: number;
}

export interface AnuncianteInput {
  codigo: string;
  nome: string;
  nif?: string;
  email?: string;
  telefone?: string;
  endereco?: string;
  paisId?: string;
  contactoNome?: string;
  contactoEmail?: string;
  contactoTelefone?: string;
  logo?: string;
  logoMimeType?: string;
  limiteCredito?: number;
  notas?: string;
  estado?: string;
}

export interface AnuncianteListResponse {
  success: boolean;
  data: Anunciante[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Stats
export interface AdStats {
  data: string;
  hora?: number;
  plataforma?: Plataforma;
  visualizacoes: number;
  cliques: number;
  custo: number;
  ctr: number;
}

export interface AdStatsSummary {
  totalVisualizacoes: number;
  totalCliques: number;
  totalCusto?: number;
  totalGasto?: number;
  totalCampanhas?: number;
  campanhasAtivas?: number;
}

export interface AdStatsResponse {
  success: boolean;
  data: AdStats[];
  summary: AdStatsSummary;
  source: 'pg_publicidade' | 'pg_pub_stats';
}

// Faturas (Invoices)
export type EstadoFatura = 'PENDENTE' | 'PAGO' | 'ANULADO' | 'VENCIDO';

export interface FaturaLinha {
  id: string;
  publicidadeId?: string;
  publicidadeTitulo?: string;
  descricao: string;
  quantidade: number;
  precoUnit: number;
  subtotal: number;
}

export interface Fatura {
  id: string;
  numero: string;
  anuncianteId: string;
  anuncianteNome?: string;
  anuncianteNif?: string;
  anuncianteEmail?: string;
  anuncianteEndereco?: string;
  periodoInicio: string;
  periodoFim: string;
  subtotal: number;
  ivaPercent: number;
  ivaValor: number;
  total: number;
  desconto: number;
  observacoes?: string;
  estado: EstadoFatura;
  dataVencimento?: string;
  dataPagamento?: string;
  metodoPagamento?: string;
  referenciaPagamento?: string;
  dataRegisto: string;
  linhas?: FaturaLinha[];
}

export interface FaturaInput {
  anuncianteId: string;
  periodoInicio: string;
  periodoFim: string;
  ivaPercent?: number;
  desconto?: number;
  observacoes?: string;
  dataVencimento?: string;
  linhas?: {
    publicidadeId?: string;
    descricao: string;
    quantidade?: number;
    precoUnit?: number;
  }[];
}

export interface FaturaSummary {
  totalFaturas: number;
  pendentes: number;
  pagas: number;
  valorPendente: number;
  valorRecebido: number;
}

export interface FaturaListResponse {
  success: boolean;
  data: Fatura[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  summary: FaturaSummary;
}

// ============================================
// SERVICE
// ============================================

class AdvertisingService {
  private basePath = '/admin/advertising';

  // ============================================
  // STATUS & MIGRATION
  // ============================================

  async checkStatus(): Promise<{ success: boolean; tables: Record<string, boolean>; allTablesExist: boolean }> {
    const response = await api.get(`${this.basePath}/status`);
    return response.data;
  }

  async setupTables(): Promise<{ success: boolean; message: string; results: string[] }> {
    const response = await api.post(`${this.basePath}/migrate`);
    return response.data;
  }

  // ============================================
  // ADVERTISEMENTS
  // ============================================

  async getAdvertisements(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    spotId?: string;
    tipoAnunciante?: TipoAnunciante;
    /** 'APP' = só as áreas das páginas de categoria; 'OUTRAS' = o resto */
    plataforma?: 'APP' | 'OUTRAS';
  }): Promise<AdvertisingListResponse> {
    const response = await api.get(this.basePath, { params });
    return response.data;
  }

  async getAdvertisementById(id: string): Promise<{ success: boolean; data: Advertisement }> {
    const response = await api.get(`${this.basePath}/${id}`);
    return response.data;
  }

  async createAdvertisement(data: AdvertisementInput): Promise<{ success: boolean; message: string; data: { id: string; codigo: string; titulo: string } }> {
    const response = await api.post(this.basePath, data);
    return response.data;
  }

  async updateAdvertisement(id: string, data: Partial<AdvertisementInput>): Promise<{ success: boolean; message: string }> {
    const response = await api.put(`${this.basePath}/${id}`, data);
    return response.data;
  }

  async deleteAdvertisement(id: string): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`${this.basePath}/${id}`);
    return response.data;
  }

  async trackAdvertisement(id: string, tipo: 'view' | 'click'): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`${this.basePath}/${id}/track`, { tipo });
    return response.data;
  }

  // ============================================
  // AD SPOTS
  // ============================================

  /** Áreas das páginas de categoria — o servidor cria/renomeia as que faltarem */
  async getAppSpots(): Promise<{ success: boolean; data: AppSpot[] }> {
    const response = await api.get(`${this.basePath}/app-spots`);
    return response.data;
  }

  async getSpots(params?: {
    status?: string;
    plataforma?: Plataforma;
    area?: string;
  }): Promise<AdSpotListResponse> {
    const response = await api.get(`${this.basePath}/spots`, { params });
    return response.data;
  }

  async createSpot(data: AdSpotInput): Promise<{ success: boolean; message: string; data: { id: string; codigo: string; nome: string } }> {
    const response = await api.post(`${this.basePath}/spots`, data);
    return response.data;
  }

  async updateSpot(id: string, data: Partial<AdSpotInput>): Promise<{ success: boolean; message: string }> {
    const response = await api.put(`${this.basePath}/spots/${id}`, data);
    return response.data;
  }

  async deleteSpot(id: string): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`${this.basePath}/spots/${id}`);
    return response.data;
  }

  // ============================================
  // ANUNCIANTES (External Advertisers)
  // ============================================

  async getAnunciantes(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
  }): Promise<AnuncianteListResponse> {
    const response = await api.get(`${this.basePath}/anunciantes`, { params });
    return response.data;
  }

  async getAnuncianteById(id: string): Promise<{ success: boolean; data: Anunciante }> {
    const response = await api.get(`${this.basePath}/anunciantes/${id}`);
    return response.data;
  }

  async createAnunciante(data: AnuncianteInput): Promise<{ success: boolean; message: string; data: { id: string; codigo: string; nome: string } }> {
    const response = await api.post(`${this.basePath}/anunciantes`, data);
    return response.data;
  }

  async updateAnunciante(id: string, data: Partial<AnuncianteInput>): Promise<{ success: boolean; message: string }> {
    const response = await api.put(`${this.basePath}/anunciantes/${id}`, data);
    return response.data;
  }

  async deleteAnunciante(id: string): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`${this.basePath}/anunciantes/${id}`);
    return response.data;
  }

  // ============================================
  // STATS (Detailed Tracking)
  // ============================================

  async getStats(params?: {
    startDate?: string;
    endDate?: string;
    publicidadeId?: string;
    anuncianteId?: string;
    plataforma?: Plataforma;
    groupBy?: 'day' | 'hour' | 'platform';
  }): Promise<AdStatsResponse> {
    const response = await api.get(`${this.basePath}/stats`, { params });
    return response.data;
  }

  async trackDetailed(publicidadeId: string, tipo: 'view' | 'click', plataforma?: Plataforma): Promise<{ success: boolean; message: string; custo: number }> {
    const response = await api.post(`${this.basePath}/stats/track`, { publicidadeId, tipo, plataforma });
    return response.data;
  }

  // ============================================
  // FATURAS (Billing)
  // ============================================

  async getFaturas(params?: {
    page?: number;
    limit?: number;
    anuncianteId?: string;
    status?: EstadoFatura;
    startDate?: string;
    endDate?: string;
  }): Promise<FaturaListResponse> {
    const response = await api.get(`${this.basePath}/faturas`, { params });
    return response.data;
  }

  async getFaturaById(id: string): Promise<{ success: boolean; data: Fatura }> {
    const response = await api.get(`${this.basePath}/faturas/${id}`);
    return response.data;
  }

  async createFatura(data: FaturaInput): Promise<{ success: boolean; message: string; data: { id: string; numero: string; total: number } }> {
    const response = await api.post(`${this.basePath}/faturas`, data);
    return response.data;
  }

  async updateFatura(id: string, data: {
    estado?: EstadoFatura;
    dataPagamento?: string;
    metodoPagamento?: string;
    referenciaPagamento?: string;
    observacoes?: string;
    desconto?: number;
    dataVencimento?: string;
  }): Promise<{ success: boolean; message: string }> {
    const response = await api.put(`${this.basePath}/faturas/${id}`, data);
    return response.data;
  }

  async generateFaturas(periodoInicio: string, periodoFim: string, anuncianteId?: string): Promise<{ success: boolean; message: string; data: { id: string; numero: string; anunciante: string; total: number }[] }> {
    const response = await api.post(`${this.basePath}/faturas/generate`, { periodoInicio, periodoFim, anuncianteId });
    return response.data;
  }

  // ============================================
  // HELPERS
  // ============================================

  getStatusLabel(status: StatusCampanha): string {
    const labels: Record<StatusCampanha, string> = {
      ativo: 'Activa',
      inativo: 'Inactiva',
      agendado: 'Agendada',
      expirado: 'Expirada',
    };
    return labels[status] || status;
  }

  getStatusColor(status: StatusCampanha): 'success' | 'default' | 'info' | 'warning' {
    const colors: Record<StatusCampanha, 'success' | 'default' | 'info' | 'warning'> = {
      ativo: 'success',
      inativo: 'default',
      agendado: 'info',
      expirado: 'warning',
    };
    return colors[status] || 'default';
  }

  getPlataformaLabel(plataforma: Plataforma): string {
    const labels: Record<Plataforma, string> = {
      WEB: 'Web',
      MOBILE: 'Mobile',
      APP: 'App (páginas)',
    };
    return labels[plataforma] || plataforma;
  }

  getAreaLabel(area: string): string {
    const labels: Record<string, string> = {
      LANDING: 'Landing Page',
      USER_DASHBOARD: 'Dashboard User',
      USER_PAGAMENTOS: 'Pagamentos',
      USER_SIDEBAR: 'Sidebar',
      USER_TRANSACOES: 'Transações',
      USER_EVENTOS: 'Eventos',
      USER_DOACOES: 'Doações',
      APP_SPLASH: 'App Splash',
      APP_HOME: 'App Home',
      APP_PAGAMENTOS: 'App Pagamentos',
      APP_NOTIF: 'Notificações',
      APP_INTERSTITIAL: 'Interstitial',
      GERAL: 'Geral',
    };
    return labels[area] || area;
  }

  getCTR(cliques: number, visualizacoes: number): string {
    if (visualizacoes === 0) return '0.00';
    return ((cliques / visualizacoes) * 100).toFixed(2);
  }

  getEstadoFaturaLabel(estado: EstadoFatura): string {
    const labels: Record<EstadoFatura, string> = {
      PENDENTE: 'Pendente',
      PAGO: 'Pago',
      ANULADO: 'Anulado',
      VENCIDO: 'Vencido',
    };
    return labels[estado] || estado;
  }

  getEstadoFaturaColor(estado: EstadoFatura): 'warning' | 'success' | 'default' | 'error' {
    const colors: Record<EstadoFatura, 'warning' | 'success' | 'default' | 'error'> = {
      PENDENTE: 'warning',
      PAGO: 'success',
      ANULADO: 'default',
      VENCIDO: 'error',
    };
    return colors[estado] || 'default';
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-CV', {
      style: 'currency',
      currency: 'CVE',
    }).format(value);
  }
}

const advertisingService = new AdvertisingService();
export default advertisingService;
